import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from '../components/Sidebar';
import TopNav from '../components/TopNav';
import useAuthStore from '../store/authStore';
import { academicService } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';

function EditCategory() {
  const navigate = useNavigate();
  const { moduleId, categoryId } = useParams();
  const { user, isAuthenticated } = useAuthStore();
  const { addNotification } = useNotification();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [module, setModule] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    order: 0,
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const isTeacher = user?.roles?.some(role => role.name?.toLowerCase() === 'teacher');
    if (!isTeacher) {
      addNotification(t('Only teachers can edit categories'), 'error');
      navigate('/courses');
      return;
    }

    fetchModuleAndCategory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate, user, moduleId, categoryId]);

  const fetchModuleAndCategory = async () => {
    try {
      const [moduleRes, categoryRes] = await Promise.all([
        academicService.getModule(moduleId),
        academicService.getCategory(categoryId),
      ]);
      setModule(moduleRes.data || moduleRes);
      const category = categoryRes.data || categoryRes;
      setFormData({
        name: category.name || '',
        description: category.description || '',
        order: category.order ?? 0,
      });
    } catch (error) {
      addNotification(t('Category not found'), 'error');
      navigate(`/modules/${moduleId}`);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = t('Category name is required');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    try {
      await academicService.updateCategory(categoryId, formData);

      addNotification(t('Category updated successfully!'), 'success');
      navigate(`/modules/${moduleId}`);
    } catch (error) {
      if (error.response?.data) {
        const errorData = error.response.data;
        setErrors(errorData);
        // Show first error message
        const firstError = Object.values(errorData)[0];
        addNotification(`${t('Failed')}: ${typeof firstError === 'string' ? firstError : JSON.stringify(firstError)}`, 'error');
      } else if (error.message) {
        addNotification(`${t('Failed to update category')}: ${error.message}`, 'error');
      } else {
        addNotification(t('Failed to update category. Please try again.'), 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!module) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={t('Edit Category')} subtitle={t('Update a category in {{module}}', { module: module.name })} />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-xl shadow-md p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('Category Name')} *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder={t('e.g., Algebra Basics')}
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('Description')}
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder={t('Describe this category...')}
                  />
                </div>

                <div>
                  <label htmlFor="order" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('Display Order')}
                  </label>
                  <input
                    type="number"
                    id="order"
                    name="order"
                    value={formData.order}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="0"
                  />
                </div>

                <div className="flex items-center justify-end space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={() => navigate(`/modules/${moduleId}`)}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                  >
                    {t('Cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? t('Saving...') : t('Save Changes')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default EditCategory;
